/**
 * The free trial, and what a workspace is allowed to do today.
 *
 * Two calls, both on our own backend:
 *
 *   - `GET /api/v1/billing/subscription/` — the plan the workspace is on, the
 *     trial it has had or is having, which of the two is holding the door open,
 *     and whether a trial is still there to be taken. This is what stands
 *     between signing up and the dashboard: `canStartTrial` decides whether the
 *     offer is shown at all.
 *   - `POST /api/v1/billing/trial/` — starting one. Nothing goes up; the token
 *     says whose it is.
 *
 * ## Nothing here is decided in the browser
 *
 * The dates the trial screens draw used to be worked out on this side, because
 * there was no endpoint to ask — see the header of `trialState`, which is what
 * is left of that. There is one now, and it answers with the two instants and
 * with `is_active`, so none of it is inferred any more: not whether the trial
 * is running, and above all not whether another one may be started. Whether a
 * second trial is allowed is a policy that will change, and a browser guessing
 * at it would offer fourteen free days the backend then refuses.
 *
 * Money and dates cross the wire the way they do everywhere else here: decimal
 * strings and ISO instants, mapped once at this boundary.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";
import { toBillingSubscription } from "./subscriptionsService";
import type {
  AccessSource,
  CurrentSubscriptionResponse,
  StartTrialResponse,
  Trial,
  TrialAccess,
  TrialResponse,
} from "../types/subscriptions.types";

const token = async (getToken: GetToken, what: string): Promise<string> => {
  const value = await getToken();
  if (!value) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error(`No active Clerk session - cannot ${what}.`);
  }
  return value;
};

/** An ISO instant as a `Date`, or `null` for one that is absent or unparseable
 *  — a bad date is worth dropping rather than drawing as "Invalid Date". */
const toDate = (raw: string | null | undefined): Date | null => {
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * The trial, out of the shape both endpoints report it in.
 *
 * `active` is the server's `is_active` and is not checked against the clock
 * here. The two would nearly always agree, and where they did not it would be
 * this side that was wrong: a laptop's clock is not evidence about a
 * subscription.
 */
export const toTrial = (
  response: TrialResponse | null | undefined
): Trial | null =>
  response
    ? {
        startedOn: toDate(response.started_at),
        endsOn: toDate(response.ends_at),
        active: !!response.is_active,
      }
    : null;

/**
 * Whole days left of the running term, or `null` for an answer that did not
 * carry one.
 *
 * Absent, null and a value that is not a finite number all read as `null`
 * rather than as zero. The difference matters: `0` is "your term ends today"
 * and is drawn loudly, while "the server did not say" must draw nothing at
 * all. Negatives are dropped the same way — a term already over is the
 * `access_source` of `NONE` that comes with it, and is `accessLapse`'s to
 * word, not a countdown's.
 */
const toDaysLeft = (raw: number | null | undefined): number | null =>
  typeof raw === "number" && Number.isFinite(raw) && raw >= 0
    ? Math.floor(raw)
    : null;

const ACCESS_SOURCES: AccessSource[] = ["SUBSCRIPTION", "FREE_TRIAL", "NONE"];

/**
 * What is holding the door open, or `null` for an answer that did not say.
 *
 * Only the three names this build knows are let through. Anything else —
 * absent, or a source the server has since grown — reads as `null` rather than
 * as `"NONE"`, because `hasAccess` treats only an explicit `"NONE"` as shut:
 * a value this app cannot name is not evidence that somebody has not paid.
 */
export const toAccessSource = (
  raw: string | null | undefined
): AccessSource | null => {
  const source = raw?.toUpperCase() as AccessSource;
  return ACCESS_SOURCES.includes(source) ? source : null;
};

/**
 * What this workspace may do today.
 *
 * A missing `can_start_trial` reads as `false`, which is the safe way round: an
 * answer that does not say is not permission, and the worst case is a dietitian
 * who has to be offered the trial from Billing rather than being walked through
 * it. The other way round would walk somebody through a flow the backend then
 * refuses at the last step.
 */
export const toTrialAccess = (
  response: CurrentSubscriptionResponse | null | undefined
): TrialAccess => ({
  subscription: response?.subscription
    ? toBillingSubscription(response.subscription)
    : null,
  trial: toTrial(response?.trial),
  accessSource: toAccessSource(response?.access_source),
  canStartTrial: response?.can_start_trial === true,
  termDaysLeft: toDaysLeft(response?.billing_state?.term?.days_left),
});

/**
 * Reads it back. Answers for a workspace that has never subscribed and never
 * had a trial too — that is the ordinary state of a brand new account, and the
 * one this whole flow exists for.
 */
export async function fetchTrialAccess(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<TrialAccess> {
  const response = await api.get<CurrentSubscriptionResponse | null>(
    API_ENDPOINTS.billing.currentSubscription,
    { token: await token(getToken, "check your subscription"), signal }
  );

  return toTrialAccess(response);
}

/**
 * Starts the fourteen days.
 *
 * Nothing goes up. There is no plan to name — the trial is the product rather
 * than a cycle of it — and who the invoice at the end will be headed with is
 * the billing profile, recorded before this is called.
 *
 * What comes back is the trial the *server* started, dates and all, and that is
 * what the confirmation reports. A response that somehow carries no trial is a
 * failure rather than a trial with no dates: this is the call that decides
 * whether a workspace is open for the next fortnight, and it should not be
 * possible to end up on a confirmation screen that cannot say what was started.
 */
export async function startTrial(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<Trial> {
  const response = await api.post<StartTrialResponse>(
    API_ENDPOINTS.billing.trial,
    {},
    { token: await token(getToken, "start your free trial"), signal }
  );

  const trial = toTrial(response?.trial);
  if (!trial) throw new Error("The trial could not be started.");

  return trial;
}
